// Вкладка «Журнал»: входы и неудачные входы, регистрация, действия с учётными записями, приглашения, настройки,
// действия администратора над чужими отчётами. Фильтры: вид событий, пользователь, поиск, период; постранично.
import { useEffect, useState } from 'react';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { adminApi, type AAudit } from './adminApi';
import { fmtDate, LoadError, Pill, useLoad } from './common';

export const ACTIONS: Record<string, [string, 'ok' | 'warn' | 'err' | 'info' | 'dim']> = {
  setup: ['Первичная настройка', 'info'], login: ['Вход', 'ok'], login_failed: ['Неудачный вход', 'err'],
  login_throttled: ['Вход приостановлен (много попыток)', 'err'], login_blocked_user: ['Вход заблокированного', 'err'],
  logout: ['Выход', 'dim'], logout_others: ['Выход на других устройствах', 'dim'], register: ['Регистрация', 'ok'],
  password_change: ['Смена пароля', 'info'], password_change_failed: ['Смена пароля: неверный текущий', 'warn'],
  user_create: ['Создан пользователь', 'info'], user_role: ['Изменена роль', 'warn'], user_block: ['Заблокирован', 'err'],
  user_unblock: ['Разблокирован', 'info'], user_update: ['Изменён пользователь', 'dim'], user_reset_password: ['Сброшен пароль', 'warn'],
  user_revoke_sessions: ['Завершены сеансы', 'warn'], user_delete: ['Удалён пользователь', 'err'], user_restore: ['Восстановлен пользователь', 'info'],
  user_purge: ['Удалён окончательно', 'err'], user_purge_expired: ['Стёрт по сроку', 'dim'],
  invite_create: ['Создано приглашение', 'info'], invite_revoke: ['Отозвано приглашение', 'dim'], settings_change: ['Изменены настройки', 'warn'],
  admin_project_read: ['Админ открыл чужой отчёт', 'warn'], admin_project_write: ['Админ изменил чужой отчёт', 'warn'],
  admin_project_trash: ['Админ удалил чужой отчёт в корзину', 'err'], admin_project_build: ['Админ собрал чужой отчёт', 'warn'],
  admin_project_zip: ['Админ скачал архив чужого отчёта', 'warn'], admin_project_download: ['Админ скачал файл чужого отчёта', 'warn'],
  admin_project_rename: ['Админ переименовал чужой отчёт', 'warn'], setup_bad_code: ['Первый запуск: неверный код', 'err'],
  admin_project_restore: ['Админ восстановил отчёт', 'info'], admin_project_purge: ['Админ удалил отчёт навсегда', 'err'],
  job_cancel: ['Отменена сборка', 'dim'],
};
const GROUPS: [string, string, string][] = [
  ['', 'Все события', ''],
  ['logins', 'Входы и выходы', 'login,logout,logout_others,setup'],
  ['fails', 'Неудачные входы', 'login_failed,login_throttled,login_blocked_user,password_change_failed,setup_bad_code'],
  ['users', 'Учётные записи', 'register,user_create,user_role,user_block,user_unblock,user_update,user_reset_password,user_revoke_sessions,user_delete,user_restore,user_purge,user_purge_expired,password_change'],
  ['invites', 'Приглашения', 'invite_create,invite_revoke'],
  ['foreign', 'Действия с чужими отчётами', 'admin_project_read,admin_project_write,admin_project_trash,admin_project_build,admin_project_zip,admin_project_download,admin_project_rename,admin_project_restore,admin_project_purge'],
  ['settings', 'Настройки и сборки', 'settings_change,job_cancel'],
];
const PERIODS: [string, string, number][] = [['all', 'за всё время', 0], ['day', 'за сутки', 1], ['week', 'за 7 дней', 7], ['month', 'за 30 дней', 30]];
const PAGE = 50;

const DETAIL_NAMES: Record<string, string> = { role: 'роль', remember: 'запомнить', wait: 'пауза, с', invite: 'приглашение', migrated: 'перенесено отчётов',
  sessions: 'сеансов', method: 'запрос', owner: 'владелец', kind: 'вид', maxUses: 'регистраций', expires: 'срок', name: 'имя', quota_mb: 'квота, МБ',
  max_projects: 'отчётов', blocked: 'блокировка' };

function details(d: any): string {
  if (!d) return '';
  if (typeof d !== 'object') return String(d);
  return Object.entries(d).map(([k, v]) => {
    let val: string;
    if (k === 'expires' && typeof v === 'number') val = fmtDate(v);
    else if (typeof v === 'boolean') val = v ? 'да' : 'нет';
    else if (Array.isArray(v)) val = v.join(', ');
    else if (v && typeof v === 'object') val = JSON.stringify(v);
    else val = String(v ?? '');
    return `${DETAIL_NAMES[k] || k}: ${val}`;
  }).join('; ');
}

export function AuditTab({ query }: { query: URLSearchParams }) {
  const [group, setGroup] = useState(query.get('group') || '');
  const [login, setLogin] = useState(query.get('login') || '');
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState('all');
  const [page, setPage] = useState(0);
  const [deb, setDeb] = useState({ login: '', q: '' });
  useEffect(() => { const t = setTimeout(() => { setDeb({ login: login.trim(), q: q.trim() }); setPage(0); }, 300); return () => clearTimeout(t); }, [login, q]);
  const days = PERIODS.find((p) => p[0] === period)![2];
  const action = GROUPS.find((g) => g[0] === group)?.[2] || '';
  const { data, error, loading, reload } = useLoad(() => adminApi.audit({ action, login: deb.login, q: deb.q,
    since: days ? Math.floor(Date.now() / 1000 - days * 86400) : undefined, limit: PAGE, offset: page * PAGE }), [action, deb, days, page]);
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const items: AAudit[] = data?.items || [];
  return (
    <section className="adm-tab" data-testid="admin-audit">
      <div className="adm-toolbar">
        <select value={group} onChange={(e) => { setGroup(e.target.value); setPage(0); }} aria-label="Вид событий" data-testid="audit-group">
          {GROUPS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <div className="search-box"><Icon name="user" size={14} />
          <input className="search adm-search-short" placeholder="Логин" value={login} onChange={(e) => setLogin(e.target.value)} data-testid="audit-login" aria-label="Логин" />
        </div>
        <div className="search-box"><Icon name="search" size={14} />
          <input className="search" placeholder="Объект, IP, подробности" value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" aria-label="Поиск в журнале" />
        </div>
        <select value={period} onChange={(e) => { setPeriod(e.target.value); setPage(0); }} aria-label="Период" data-testid="audit-period">
          {PERIODS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <span className="topbar-spacer" />
        <Btn icon="refresh" title="Обновить" onClick={() => void reload()} className={loading ? 'is-loading' : ''} />
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-table-wrap">
        <table className="adm-table is-compact" data-testid="audit-table">
          <thead><tr><th>Время</th><th>Кто</th><th>Событие</th><th>Объект</th><th>Подробности</th><th>IP</th></tr></thead>
          <tbody>
            {items.map((it) => {
              const [label, tone] = ACTIONS[it.action] || [it.action, 'dim' as const];
              return (
                <tr key={it.id} data-testid="audit-row" data-action={it.action}>
                  <td className="adm-nowrap" title={new Date(it.ts * 1000).toLocaleString('ru-RU')}>{fmtDate(it.ts)}</td>
                  <td>{it.login ? <button type="button" className="adm-link" onClick={() => setLogin(it.login)} title="Только этот пользователь">{it.login}</button> : <span className="adm-dim">—</span>}</td>
                  <td><Pill tone={tone}>{label}</Pill></td>
                  <td className="adm-target">{it.target || <span className="adm-dim">—</span>}</td>
                  <td className="adm-details">{details(it.details)}</td>
                  <td className="mono adm-dim">{it.ip}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && !items.length && <div className="adm-empty"><Icon name="activity" size={26} /><div>Событий не найдено</div></div>}
      </div>
      {total > 0 && (
        <div className="adm-foot adm-pager">
          <span>Событий: {total}</span>
          <span className="topbar-spacer" />
          <Btn icon="chevron-left" title="Назад" disabled={page === 0} onClick={() => setPage(page - 1)} testid="audit-prev" />
          <span>стр. {page + 1} из {pages}</span>
          <Btn icon="chevron-right" title="Дальше" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} testid="audit-next" />
        </div>
      )}
    </section>
  );
}
